import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getPreferences, savePreferences } from "@/lib/preferences";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Clock3, MonitorCog, Save } from "lucide-react";
import { toast } from "sonner";

const TIMEZONES = [
  ["America/Santo_Domingo", "Santo Domingo (UTC-4)"],
  ["America/Puerto_Rico", "Puerto Rico (UTC-4)"],
  ["America/New_York", "Nueva York"],
  ["America/Panama", "Panama (UTC-5)"],
  ["UTC", "UTC"],
];

export default function Configuracion() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [theme, setTheme] = useState(getPreferences().theme);
  const [clock, setClock] = useState(new Date());

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/general");
    setSettings(data);
    setClock(new Date(data.server_time));
    savePreferences({ timezone: data.timezone, date_format: data.date_format, time_format: data.time_format });
  }, []);

  useEffect(() => { load().catch(() => toast.error("No se pudo cargar la configuracion")); }, [load]);
  useEffect(() => {
    const timer = setInterval(() => setClock((current) => new Date(current.getTime() + 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  const changeTheme = (value) => {
    setTheme(value);
    savePreferences({ theme: value });
  };

  const save = async () => {
    try {
      const { data } = await api.put("/settings/general", settings);
      setSettings(data);
      savePreferences({ timezone: data.timezone, date_format: data.date_format, time_format: data.time_format, theme });
      toast.success("Configuracion guardada");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  if (!settings) return <div className="text-slate-500">Cargando...</div>;

  const displayedTime = new Intl.DateTimeFormat("es-DO", {
    dateStyle: "full", timeStyle: "medium", timeZone: settings.timezone,
    hour12: settings.time_format === "12h",
  }).format(clock);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Configuracion</h1>
        <p className="text-slate-500 mt-1">Hora operativa, formato y apariencia de la aplicacion</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 border border-slate-200 bg-white space-y-5">
          <div className="flex items-center gap-2"><Clock3 size={18} /><h2 className="font-semibold text-slate-800">Fecha y hora</h2></div>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-4 font-mono text-sm" data-testid="settings-clock">{displayedTime}</div>
          <div>
            <Label>Zona horaria</Label>
            <Select value={settings.timezone} onValueChange={(timezone) => setSettings({ ...settings, timezone })} disabled={!isAdmin}>
              <SelectTrigger className="mt-2" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Formato de fecha</Label><Select value={settings.date_format} onValueChange={(date_format) => setSettings({ ...settings, date_format })} disabled={!isAdmin}><SelectTrigger className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="DD/MM/YYYY">DD/MM/AAAA</SelectItem><SelectItem value="MM/DD/YYYY">MM/DD/AAAA</SelectItem></SelectContent></Select></div>
            <div><Label>Formato de hora</Label><Select value={settings.time_format} onValueChange={(time_format) => setSettings({ ...settings, time_format })} disabled={!isAdmin}><SelectTrigger className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="24h">24 horas</SelectItem><SelectItem value="12h">12 horas</SelectItem></SelectContent></Select></div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div><Label>Hora automatica del servidor</Label><p className="text-xs text-slate-500 mt-1">Activa y administrada por Windows.</p></div>
            <Switch checked disabled />
          </div>
          <div><Label>Servidor de referencia</Label><Input className="mt-2" value={settings.time_server} onChange={(event) => setSettings({ ...settings, time_server: event.target.value })} disabled={!isAdmin || !settings.automatic_time} /><p className="text-xs text-slate-500 mt-1">La sincronizacion del reloj del equipo se administra en Windows; la app no cambia la hora del sistema.</p></div>
        </Card>

        <Card className="p-5 border border-slate-200 bg-white space-y-5">
          <div className="flex items-center gap-2"><MonitorCog size={18} /><h2 className="font-semibold text-slate-800">Apariencia</h2></div>
          <div><Label>Tema en este dispositivo</Label><Select value={theme} onValueChange={changeTheme}><SelectTrigger className="mt-2" data-testid="settings-theme"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="light">Claro</SelectItem><SelectItem value="dark">Oscuro</SelectItem></SelectContent></Select></div>
          {isAdmin && <div><Label>Tema predeterminado</Label><Select value={settings.default_theme} onValueChange={(default_theme) => setSettings({ ...settings, default_theme })}><SelectTrigger className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="light">Claro</SelectItem><SelectItem value="dark">Oscuro</SelectItem></SelectContent></Select></div>}
          <p className="text-sm text-slate-500">El tema del dispositivo es personal. La zona horaria y los formatos son generales y solo Admin puede modificarlos.</p>
        </Card>
      </div>

      {isAdmin && <div className="flex justify-end"><Button onClick={save} data-testid="settings-save"><Save size={16} className="mr-2" />Guardar</Button></div>}
    </div>
  );
}
